/**
 * Sender and author on Svar, Videresend, and a new letter.
 * The current clinic is always an authorised sender. Extra identities come
 * from clinic_sender_identities. The opened letter's sender is never used
 * as our sender. Organisation names come from the server list, not the client.
 */

export type ClinicSenderSource = {
  sorId?: string | null;
  name?: string | null;
  ean?: string | null;
};

export type ClinicSender = {
  sorId: string;
  name: string;
  gln: string;
};

export type LetterParties = {
  senderSor?: string | null;
  senderOrganization?: string | null;
  receiverSor?: string | null;
  receiverOrganization?: string | null;
  receiverGln?: string | null;
  senderPractitionerName?: string | null;
};

export const AUTHOR_FREE_TEXT = '__free_text__';

export function composeClinicSender(clinic: ClinicSenderSource): ClinicSender {
  return {
    sorId: blank(clinic.sorId),
    name: blank(clinic.name),
    gln: blank(clinic.ean),
  };
}

/** Identities this clinic may send as. The connection is 1:1 (one SOR + EAN). */
export function clinicAuthorizedSenders(
  connection: ClinicSenderSource,
  extras: readonly ClinicSenderSource[] = [],
): ClinicSender[] {
  const identities: ClinicSender[] = [];
  const primary = composeClinicSender(connection);
  if (primary.sorId) identities.push(primary);
  for (const extra of extras) {
    const identity = composeClinicSender(extra);
    if (!identity.sorId) continue;
    if (identities.some((item) => item.sorId === identity.sorId)) continue;
    identities.push(identity);
  }
  return identities;
}

export function isAuthorizedClinicSender(
  requested: { sorId?: string | null; ean?: string | null; gln?: string | null },
  authorized: readonly ClinicSender[],
): boolean {
  const sor = requested.sorId?.trim() ?? '';
  if (!sor) return false;
  const ean = (requested.ean ?? requested.gln)?.trim() ?? '';
  return authorized.some((identity) => {
    if (identity.sorId !== sor) return false;
    if (ean && identity.gln && ean !== identity.gln) return false;
    return true;
  });
}

/**
 * Sender for a reply or forward. Always includes the clinic. On a received
 * letter, the receiver organisation is added only when it is authorised.
 */
export function senderForReplyOrForward(
  clinic: ClinicSenderSource,
  letter: LetterParties | null | undefined,
): ClinicSender {
  void letter;
  return composeClinicSender(clinic);
}

export function replySenderChoices(input: {
  mode: 'new' | 'reply' | 'forward';
  direction: 'in' | 'out' | null;
  clinic: ClinicSender;
  letterReceiver: ClinicSenderSource | null | undefined;
  authorized: readonly ClinicSender[];
}): ClinicSender[] {
  switch (input.mode) {
    case 'new':
    case 'reply':
    case 'forward':
      return input.authorized.filter((item) => item.sorId);
    default: {
      const unexpected: never = input.mode;
      return unexpected;
    }
  }
}

/** On Svar of a received letter, the letter's receiver when that identity is allowed. */
export function preferredReplySender(input: {
  mode: 'new' | 'reply' | 'forward';
  direction: 'in' | 'out' | null;
  clinic: ClinicSender;
  letterReceiver: ClinicSenderSource | null | undefined;
  authorized: readonly ClinicSender[];
}): ClinicSender {
  const clinic = input.clinic.sorId ? input.clinic : input.authorized[0] ?? input.clinic;
  switch (input.mode) {
    case 'new':
    case 'forward':
      return clinic;
    case 'reply': {
      if (input.direction !== 'in') return clinic;
      const receiver = composeClinicSender(input.letterReceiver ?? {});
      if (!receiver.sorId || !isAuthorizedClinicSender(receiver, input.authorized)) {
        return clinic;
      }
      return input.authorized.find((item) => item.sorId === receiver.sorId) ?? clinic;
    }
    default: {
      const unexpected: never = input.mode;
      return unexpected;
    }
  }
}

/**
 * Same rule as communicate-send: keep an authorised requested sender, otherwise
 * overwrite with the current clinic.
 */
export function resolveOutgoingSender(
  requested: { sorId?: string | null; name?: string | null; gln?: string | null },
  clinic: ClinicSenderSource,
  authorized: readonly ClinicSender[] = clinicAuthorizedSenders(clinic),
): ClinicSender {
  const clinicSender = composeClinicSender(clinic);
  if (!isAuthorizedClinicSender(requested, authorized)) return clinicSender;
  const match = authorized.find((item) => item.sorId === requested.sorId?.trim());
  return {
    sorId: match?.sorId || clinicSender.sorId,
    name: match?.name || clinicSender.name,
    gln: match?.gln || clinicSender.gln,
  };
}

export function authorFromSignedInUser(
  user: { name?: string | null; phone?: string | null } | null | undefined,
  clinic?: { phone?: string | null } | null,
): { family: string; given: string; phone: string } {
  const { family, given } = splitPersonName(user?.name);
  return { family, given, phone: blank(user?.phone) || blank(clinic?.phone) };
}

export function authorFromClinicUser(
  user: { name?: string | null; phone?: string | null } | null | undefined,
  clinic?: { phone?: string | null } | null,
): { family: string; given: string; phone: string } {
  return authorFromSignedInUser(user, clinic);
}

function splitPersonName(name: string | null | undefined): { family: string; given: string } {
  const parts = (name ?? '')
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);
  if (parts.length === 0) return { family: '', given: '' };
  if (parts.length === 1) return { family: parts[0] ?? '', given: '' };
  return {
    family: parts[parts.length - 1] ?? '',
    given: parts.slice(0, -1).join(' '),
  };
}

function blank(value: string | null | undefined): string {
  return value?.trim() ?? '';
}
